'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from './DestinationLink'

import * as React from 'react'
import * as TogglePrimitive from '@radix-ui/react-toggle'
import { Award, TrendingDown } from '@/components/homepage-october/lucide'

import { cn, formatPrice } from '@/lib/utils'
import { Heart, StarRow } from './icons'
import { useShop, type ShopItem } from '../state'
import type { Product } from '../types'
import { Img } from './Img'
import { IdChip } from './layout'
import { RetailerMark } from '@/components/homepage-2026/retailer-mark'

export function priceOf(p: Product) {
  if (p.price) return p.price
  if (p.priceCents != null && p.priceCents !== '') return formatPrice(Number(p.priceCents) / 100)
  return ''
}
export function ctaOf(p: Product) {
  return p.cta ?? { text: 'Get Deal', href: p.href, aria: `Get Deal: ${p.brand} ${p.title}` }
}
export function shopItem(p: Product): ShopItem {
  return { id: p.id, fin: p.fin, priceCents: Number(p.priceCents) || 0, brand: p.brand, title: p.title, href: p.href, img: p.img?.src, price: priceOf(p) }
}
const short = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s)

export function Stars({ label, score, count }: { label: string; score: string; count: string }) {
  const pct = Math.max(0, Math.min(100, (parseFloat(score) / 5) * 100))
  return (
    <div className={compactClassName("flex items-center gap-1.5 text-xs text-neutral-600")}>
      <span role="img" aria-label={label} className={compactClassName("relative inline-flex")}>
        <StarRow className="text-neutral-300" />
        <StarRow className="absolute inset-0 text-[#f59e0b]" style={{ width: `${pct}%` }} />
      </span>
      <span aria-hidden="true" className={compactClassName("font-semibold text-neutral-800")}>{score}</span>
      <span aria-hidden="true">({count})</span>
    </div>
  )
}

// Shared card classes preserve the approved styles without repeating utility lists.
// Save uses the Radix toggle; Compare uses one native checkbox with its associated label.
export function SaveToggle({ p }: { p: Product }) {
  const shop = useShop()
  const on = shop.isSaved(p)
  return (
    <TogglePrimitive.Root aria-label={`Save ${p.brand} ${p.model || p.title}`} pressed={on} onPressedChange={() => shop.toggleSaved(shopItem(p))} className={compactClassName("v3-pc-save")}>
      <Heart className="size-4" />
    </TogglePrimitive.Root>
  )
}

export function CompareBox({ p, className }: { p: Product; className?: string }) {
  const shop = useShop()
  const id = React.useId()
  return (
    <label htmlFor={id} className={compactClassName(cn('flex min-h-8 items-center gap-2', className))}>
      <input
        type="checkbox"
        id={id}
        checked={shop.isCompared(p)}
        onChange={() => shop.toggleCompare(shopItem(p))}
        aria-label={`Compare ${p.brand} ${short(p.title, 40)}`}
        className={compactClassName("v3-pc-check")}
      />
      <span className={compactClassName("v3-pc-check-label")}>Compare</span>
    </label>
  )
}

/** The product card used in every rail and grid. Same facts as v2's card, drawn on shadcn Card. */
export function ProductCard({ p, className, eager, headingLevel = 3 }: { p: Product; className?: string; eager?: boolean; headingLevel?: 3 | 4 }) {
  const cta = ctaOf(p)
  const price = priceOf(p)
  const star = p.energyStar || (p.badge && /energy star/i.test(p.badge.text) ? p.badge.text : null)
  const H = headingLevel === 3 ? 'h3' : 'h4'
  return (
    <div data-fin={p.fin} className={compactClassName(cn('v3-pc group/card', className))}>
      <div className={compactClassName("relative")}>
        <DestinationLink href={p.href} tabIndex={-1} aria-hidden="true" className="v3-pc-media">
          {p.img ? <Img src={p.img.src} alt="" eager={eager} className="v3-pc-photo" style={p.img.scale ? { transform: `scale(${p.img.scale})` } : undefined} /> : null}
          {p.imgText ? <span className={compactClassName("sr-only")}>{p.imgText}</span> : null}
        </DestinationLink>
        <div className={compactClassName("v3-pc-flags")}>
          {p.drop || p.award || p.badge ? <div className={compactClassName("flex flex-col items-start gap-1.5")}>
            {p.drop ? (
              <span className={compactClassName("v3-pc-drop")}>
                <TrendingDown aria-hidden="true" className="size-3.5" />
                {p.drop}
              </span>
            ) : null}
            {p.award ? (
              <span className={compactClassName("v3-pc-award")}>
                <Award aria-hidden="true" />
                {p.award}
              </span>
            ) : null}
          </div> : null}
          <SaveToggle p={p} />
        </div>
      </div>
      <div className={compactClassName("v3-pc-body")}>
        <div className={compactClassName("flex items-center justify-between gap-2")}>
          <span className={compactClassName("v3-pc-brand")}>{p.brand}</span>
          {p.kind ? <span className={compactClassName("v3-pc-kind")}>{p.kind}</span> : null}
        </div>
        <H className={compactClassName("v3-pc-title")}>
          <DestinationLink href={p.href} aria-label={p.titleAria}>{p.title}</DestinationLink>
        </H>
        <div className={compactClassName("v3-pc-meta")}>
          <IdChip>{p.fin}</IdChip>
          {p.model ? <span className={compactClassName("min-w-0 truncate")}>· Model {p.model}</span> : null}
        </div>
        {p.rating || p.reviews ? <div className={compactClassName("v3-pc-rating")}>{p.rating ? <Stars {...p.rating} /> : (
          <p className={compactClassName("text-xs text-neutral-500")}>{p.reviews} retailer review{p.reviews === 1 ? '' : 's'} so far</p>
        )}</div> : null}
        {/* Certification leads the specifications and stays together at narrow card widths. */}
          {star || p.dims || p.energy ? <p className={compactClassName("v3-pc-specs tabular text-[11px] leading-5 text-neutral-600")}>
            {star ? <><span className={compactClassName("v3-energy-star")}>ENERGY STAR</span>{' '}</> : null}
            {p.dims ? <span>{p.dims}</span> : null}
            {p.energy ? <span>{p.energy.text}{p.energy.sr ? <span className={compactClassName("sr-only")}> {p.energy.sr}</span> : null}</span> : null}
          </p> : null}
        <div className={compactClassName("mt-auto pt-2")}>
          <div className={compactClassName("flex items-baseline gap-2")}>
            {price ? <span className={compactClassName("v3-pc-price")}>{price}</span> : <span className={compactClassName("text-sm font-semibold text-neutral-600")}>{p.unavailable ? 'Check availability' : 'See current price'}</span>}
            {p.was ? <span className={compactClassName("v3-pc-was")}>{p.was}</span> : null}
          </div>
          {p.store ? (
            <p className={compactClassName("v3-pc-store")}>
              <RetailerMark name={p.store} />
              <span>Lowest at <b className={compactClassName("font-semibold text-neutral-800")}>{p.store}</b></span>
            </p>
          ) : <p className={compactClassName("v3-pc-store text-neutral-500")}>No in-stock offer verified</p>}
          <DestinationLink href={cta.href} aria-label={`${cta.text}: ${p.brand} ${p.model || p.title}`} className="v3-pc-cta">{cta.text}</DestinationLink>
          <CompareBox p={p} className="mt-3" />
        </div>
      </div>
    </div>
  )
}

/** A compact row (quiz results, compare and saved sheets). */
export function ProductRow({ p, className }: { p: { fin: string; brand: string; title: string; href: string; img?: string; price?: string }; className?: string }) {
  return (
    <DestinationLink href={p.href} className={cn('flex items-center gap-3 rounded-lg border bg-white p-2.5 transition-colors hover:border-[#0469f0]/40 hover:bg-[#f8fbff]', className)}>
      <span className={compactClassName("flex size-14 shrink-0 items-center justify-center rounded-md bg-white")}>
        {p.img ? <Img src={p.img} alt="" className="size-full" /> : null}
      </span>
      <span className={compactClassName("flex min-w-0 flex-col gap-0.5")}>
        <span className={compactClassName("line-clamp-2 text-[13px] font-medium leading-snug text-neutral-900")}><span className={compactClassName("text-[#0454c0]")}>{p.brand}</span> {p.title}</span>
        <span className={compactClassName("flex items-center gap-2 text-[11px] text-neutral-500")}><IdChip>{p.fin}</IdChip>{p.price ? <b className={compactClassName("tabular text-sm text-neutral-950")}>{p.price}</b> : null}</span>
      </span>
    </DestinationLink>
  )
}
